import { Button, Input, Label } from '@features/ui';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import React from 'react';

const schema = yup.object({
  oldPassword: yup.string().required('Old password is required'),
  password: yup.string().required('Password is required'),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .test('passwords-match', 'Passwords must match', function (value) {
      return this.parent.password === value;
    }),
});

export type ResetPasswordFormData = yup.InferType<typeof schema>;

type Props = {
  onSubmit: (data: ResetPasswordFormData) => void;
};

export const ResetPasswordForm: React.FC<Props> = ({ onSubmit }) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      oldPassword: '',
      password: '',
      confirmPassword: '',
    },
  });

  const handleSubmitForm = (data: ResetPasswordFormData) => {
    onSubmit(data);
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleSubmitForm)}>
      <h2 className="text-[23px] font-medium mb-5">
        {t('common.form.resetPassword')}
      </h2>
      <div className="bg-card rounded-xl p-6">
        <div className="space-y-5 lg:max-w-md">
          <Controller
            control={control}
            name="oldPassword"
            render={({ field }) => (
              <div className="space-y-2.5">
                <Label htmlFor="oldPassword">
                  {t('common.form.oldPassword')}
                </Label>
                <Input
                  className={'rounded-[10px] border-[#979797]'}
                  error={!!errors.oldPassword}
                  id="oldPassword"
                  placeholder={t('common.form.password')}
                  type="password"
                  {...field}
                />
                {errors.oldPassword && (
                  <p className="text-destructive text-xs">
                    {errors.oldPassword.message}
                  </p>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="password"
            render={({ field }) => (
              <div className="space-y-2.5">
                <Label htmlFor="newPassword">
                  {t('common.form.newPassword')}
                </Label>
                <Input
                  className={'rounded-[10px] border-[#979797]'}
                  error={!!errors.password}
                  id="newPassword"
                  placeholder={t('common.form.password')}
                  type="password"
                  {...field}
                />
                {errors.password && (
                  <p className="text-destructive text-xs">
                    {errors.password.message}
                  </p>
                )}
              </div>
            )}
          />

          <Controller
            control={control}
            name="confirmPassword"
            render={({ field }) => (
              <div className="space-y-2.5">
                <Label htmlFor="confirmPassword">
                  {t('common.form.confirmPassword')}
                </Label>
                <Input
                  className={'rounded-[10px] border-[#979797]'}
                  error={!!errors.confirmPassword}
                  id="confirmPassword"
                  placeholder={t('common.form.password')}
                  type="password"
                  {...field}
                />
                {errors.confirmPassword && (
                  <p className="text-destructive text-xs">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            )}
          />

          <Button className="bg-red-600 hover:bg-red-700 text-white w-full">
            {t('common.form.updatePassword')}
          </Button>
        </div>
      </div>
    </form>
  );
};
